<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>IABF {{ adminFestivalYear }} Carpools</h2>
            <div class="mb-3">
              <v-btn color="primary" class="mr-2" @click="showAddDialog = true">+ Add Slot</v-btn>
              <v-btn to="/iabf/admin/carpools/print" outlined color="primary" class="mr-2">🖨 Driver sheets</v-btn>
              <v-btn
                v-if="slots.length === 0"
                color="warning"
                :loading="seeding"
                @click="seedSlots"
              >
                Seed Slots from Last Year
              </v-btn>
            </div>

            <!-- MISSING CARPOOL -->
            <div v-if="missingCarpoolEntries.length" class="missing-panel">
              <div class="missing-head">
                <div>
                  <strong>{{ missingCarpoolEntries.length }} dancers haven't joined a carpool</strong>
                  <span class="missing-sub">(driving-both-ways dancers excluded)</span>
                </div>
                <div class="missing-actions">
                  <v-btn small color="primary" @click="copyMissingEmails">
                    {{ copiedMissing ? '✓ Copied' : '📋 Copy emails' }}
                  </v-btn>
                  <v-btn small text @click="showMissingDetails = !showMissingDetails">
                    {{ showMissingDetails ? 'Hide list' : 'Show list' }}
                  </v-btn>
                </div>
              </div>
              <ul v-if="showMissingDetails" class="missing-list">
                <li v-for="d in missingCarpoolEntries" :key="d.id">
                  <span class="missing-name">{{ d.name }}</span>
                  <span class="missing-email">{{ d.email }}</span>
                  <span class="missing-travel">
                    arr: {{ d.aType || '—' }} · dep: {{ d.dType || '—' }}
                  </span>
                </li>
              </ul>
            </div>

            <!-- ARRIVALS -->
            <h3 class="section-heading">Arrivals</h3>
            <v-data-table
              :headers="headers"
              :items="arrivalRegularSlots"
              :items-per-page="50"
              hide-default-footer
              class="mb-4"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.date }}</td>
                  <td>{{ item.airport }}</td>
                  <td>{{ item.time }}</td>
                  <td>${{ item.pricePerPerson }}</td>
                  <td>
                    <div v-for="d in (item.dancers || [])" :key="d.id" class="dancer-row">
                      <span class="dancer-name">{{ d.name }}</span>
                      <span v-if="isGuestRider(d)" class="faculty-tag">faculty</span>
                      <span v-if="!isGuestRider(d) && riderFlightTime(d, 'arrival')" class="dancer-flight">· lands {{ riderFlightTime(d, 'arrival') }}</span>
                      <span v-if="d.dropoff" class="dancer-location">· {{ d.dropoff === 'atlantic' ? 'ACA' : 'SpringHill Suites' }}</span>
                      <v-btn x-small icon color="red" :loading="removingId === d.id + item.id" @click="removeDancer(item, d)">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                      <div v-if="isGuestRider(d)" class="faculty-edit">
                        <input
                          type="tel"
                          class="faculty-input"
                          :value="d.phone || ''"
                          placeholder="Phone"
                          @blur="saveRiderPhone(item, d, $event.target.value)"
                          @keydown.enter="$event.target.blur()"
                        />
                        <input
                          type="text"
                          class="faculty-input"
                          :value="d.guestFlightTime || ''"
                          placeholder="Flight time"
                          @blur="saveRiderFlightTime(item, d, $event.target.value)"
                          @keydown.enter="$event.target.blur()"
                        />
                      </div>
                    </div>
                    <v-btn x-small text color="blue darken-1" @click="openAddDancer(item)">+ Add dancer</v-btn>
                  </td>
                  <td>{{ item.dancers ? item.dancers.length : 0 }}/{{ item.maxSeats }}</td>
                  <td>
                    <v-chip x-small color="green" dark>Active</v-chip>
                  </td>
                  <td>
                    <v-btn x-small icon @click="editSlot(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                    <v-btn x-small icon color="red" @click="confirmDelete(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- ARRIVAL WAITLIST -->
            <div v-if="arrivalWaitlistSlot" class="waitlist-section mb-8">
              <div class="waitlist-section-label">
                Arrival Waitlist
                <v-btn x-small text color="blue darken-1" class="ml-2" @click="openAddDancer(arrivalWaitlistSlot)">+ Add to waitlist</v-btn>
              </div>
              <div class="waitlist-cards">
                <div
                  v-for="entry in arrivalWaitlistEntries"
                  :key="entry.id"
                  class="waitlist-card"
                >
                  <div class="waitlist-card-name">{{ entry.name }}</div>
                  <div class="waitlist-card-details">
                    <span v-if="entry.airport" class="waitlist-card-airport">{{ entry.airport }}</span>
                    <span v-else class="waitlist-card-airport grey--text">Airport unknown</span>
                    <span class="waitlist-card-sep">·</span>
                    <span v-if="entry.requestedTime">Wants to leave at <strong>{{ entry.requestedTime }}</strong></span>
                    <span v-else class="grey--text">No time specified</span>
                  </div>
                  <div v-if="entry.dropoff" class="waitlist-card-location">
                    Drop-off: {{ entry.dropoff === 'atlantic' ? 'Atlantic Center for the Arts' : 'SpringHill Suites' }}
                  </div>
                  <div class="mt-2 d-flex">
                    <v-btn
                      small
                      color="primary"
                      @click="openMakeCarpool(arrivalWaitlistSlot, entry, 'arrival')"
                    >Make Carpool</v-btn>
                    <v-btn
                      x-small
                      icon
                      color="red"
                      class="ml-1"
                      :loading="removingId === entry.id + arrivalWaitlistSlot.id"
                      @click="removeDancer(arrivalWaitlistSlot, entry)"
                    ><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                </div>
              </div>
            </div>

            <!-- DEPARTURES -->
            <h3 class="section-heading">Departures</h3>
            <v-data-table
              :headers="headers"
              :items="departureRegularSlots"
              :items-per-page="50"
              hide-default-footer
              class="mb-4"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>{{ item.date }}</td>
                  <td>{{ item.airport }}</td>
                  <td>
                    {{ item.time }}
                    <div v-if="computedArrivalTime(item)" class="drive-time-note">
                      → airport ~{{ computedArrivalTime(item) }} ({{ driveTimeLabel(item.airport) }} drive)
                    </div>
                  </td>
                  <td>${{ item.pricePerPerson }}</td>
                  <td>
                    <div v-for="d in (item.dancers || [])" :key="d.id" class="dancer-row">
                      <span class="dancer-name">{{ d.name }}</span>
                      <span v-if="isGuestRider(d)" class="faculty-tag">faculty</span>
                      <span v-if="!isGuestRider(d) && riderFlightTime(d, 'departure')" class="dancer-flight">· flight {{ riderFlightTime(d, 'departure') }}</span>
                      <span v-if="d.pickup" class="dancer-location">· {{ d.pickup === 'atlantic' ? 'ACA' : 'SpringHill Suites' }}</span>
                      <v-btn x-small icon color="red" :loading="removingId === d.id + item.id" @click="removeDancer(item, d)">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                      <div v-if="isGuestRider(d)" class="faculty-edit">
                        <input
                          type="tel"
                          class="faculty-input"
                          :value="d.phone || ''"
                          placeholder="Phone"
                          @blur="saveRiderPhone(item, d, $event.target.value)"
                          @keydown.enter="$event.target.blur()"
                        />
                        <input
                          type="text"
                          class="faculty-input"
                          :value="d.guestFlightTime || ''"
                          placeholder="Flight time"
                          @blur="saveRiderFlightTime(item, d, $event.target.value)"
                          @keydown.enter="$event.target.blur()"
                        />
                      </div>
                    </div>
                    <v-btn x-small text color="blue darken-1" @click="openAddDancer(item)">+ Add dancer</v-btn>
                  </td>
                  <td>{{ item.dancers ? item.dancers.length : 0 }}/{{ item.maxSeats }}</td>
                  <td>
                    <v-chip x-small color="green" dark>Active</v-chip>
                  </td>
                  <td>
                    <v-btn x-small icon @click="editSlot(item)"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                    <v-btn x-small icon color="red" @click="confirmDelete(item)"><v-icon x-small>mdi-delete</v-icon></v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- DEPARTURE WAITLIST -->
            <div v-if="departureWaitlistSlot" class="waitlist-section">
              <div class="waitlist-section-label">
                Departure Waitlist
                <v-btn x-small text color="blue darken-1" class="ml-2" @click="openAddDancer(departureWaitlistSlot)">+ Add to waitlist</v-btn>
              </div>
              <div class="waitlist-cards">
                <div
                  v-for="entry in departureWaitlistEntries"
                  :key="entry.id"
                  class="waitlist-card"
                >
                  <div class="waitlist-card-name">{{ entry.name }}</div>
                  <div class="waitlist-card-details">
                    <span v-if="entry.airport" class="waitlist-card-airport">{{ entry.airport }}</span>
                    <span v-else class="waitlist-card-airport grey--text">Airport unknown</span>
                    <span class="waitlist-card-sep">·</span>
                    <span v-if="entry.requestedTime">Wants to leave at <strong>{{ entry.requestedTime }}</strong></span>
                    <span v-else class="grey--text">No time specified</span>
                  </div>
                  <div v-if="entry.pickup" class="waitlist-card-location">
                    Pick-up: {{ entry.pickup === 'atlantic' ? 'Atlantic Center for the Arts' : 'SpringHill Suites' }}
                  </div>
                  <div class="mt-2 d-flex">
                    <v-btn
                      small
                      color="primary"
                      @click="openMakeCarpool(departureWaitlistSlot, entry, 'departure')"
                    >Make Carpool</v-btn>
                    <v-btn
                      x-small
                      icon
                      color="red"
                      class="ml-1"
                      :loading="removingId === entry.id + departureWaitlistSlot.id"
                      @click="removeDancer(departureWaitlistSlot, entry)"
                    ><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                </div>
              </div>
            </div>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>

    <!-- Make Carpool Dialog -->
    <v-dialog v-model="showMakeCarpoolDialog" max-width="480">
      <v-card v-if="makeCarpoolEntry">
        <v-card-title>Make Carpool for {{ makeCarpoolEntry.name }}</v-card-title>
        <v-card-text>
          <p class="body-2 mb-3">This will create a new carpool slot and automatically add {{ makeCarpoolEntry.name }} to it.</p>
          <v-text-field v-model="makeCarpoolForm.date" label="Date (e.g. Monday, July 6)" class="mb-2" />
          <v-select
            v-model="makeCarpoolForm.airport"
            :items="['MCO', 'DAB']"
            label="Airport"
            class="mb-2"
            @change="setMakeCarpoolPrice"
          />
          <v-text-field v-model="makeCarpoolForm.time" label="Departure time (e.g. 9:00am)" class="mb-2" />
          <v-text-field v-model.number="makeCarpoolForm.pricePerPerson" label="Price per person ($)" type="number" class="mb-2" />
          <v-text-field v-model.number="makeCarpoolForm.maxSeats" label="Max seats" type="number" class="mb-2" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showMakeCarpoolDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="makingCarpool" @click="confirmMakeCarpool">Create Carpool</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Dancer to Slot Dialog -->
    <v-dialog v-model="showDancerDialog" max-width="440">
      <v-card v-if="targetSlot">
        <v-card-title>
          {{ targetSlot.isWaitlist ? 'Add to waitlist' : 'Add to carpool' }}
        </v-card-title>
        <v-card-subtitle v-if="!targetSlot.isWaitlist">{{ targetSlot.date }} · {{ targetSlot.airport }} · {{ targetSlot.time }}</v-card-subtitle>
        <v-card-text>
          <v-switch
            v-model="addDancerForm.isGuest"
            label="Adding faculty or guest (not a registered dancer)"
            class="mt-0"
            hide-details
          />
          <v-text-field
            v-if="addDancerForm.isGuest"
            v-model="addDancerForm.guestName"
            label="Name (e.g. Jane Smith — Faculty)"
            class="mb-2 mt-3"
          />
          <v-autocomplete
            v-else
            v-model="addDancerForm.dancer"
            :items="dancerOptions"
            :item-text="d => `${d.firstName || ''} ${d.lastName || ''}`.trim()"
            label="Search dancer"
            return-object
            clearable
            class="mb-2 mt-3"
          />
          <v-select
            v-if="!targetSlot.isWaitlist"
            v-model="addDancerForm.location"
            :items="locationOptions"
            label="Pickup / drop-off location"
            class="mb-2"
          />
          <template v-if="targetSlot.isWaitlist">
            <v-select
              v-model="addDancerForm.airport"
              :items="['MCO', 'DAB']"
              label="Airport (optional)"
              clearable
              class="mb-2"
            />
            <v-text-field
              v-model="addDancerForm.requestedTime"
              label="Requested time (optional)"
              class="mb-2"
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDancerDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!canSubmitAddDancer"
            :loading="addingDancer"
            @click="addDancer"
          >Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Slot Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500">
      <v-card>
        <v-card-title>{{ editingSlot ? 'Edit Slot' : 'Add Carpool Slot' }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="form.direction"
            :items="['arrival', 'departure']"
            label="Direction"
            class="mb-2"
          />
          <v-text-field v-model="form.date" label="Date (e.g. Monday, July 6)" class="mb-2" />
          <v-select
            v-model="form.airport"
            :items="['MCO', 'DAB']"
            label="Airport"
            class="mb-2"
            @change="setPrice"
          />
          <v-text-field v-model="form.time" label="Time (e.g. 2:00 PM or 8:00am)" class="mb-2" />
          <v-text-field v-model.number="form.pricePerPerson" label="Price per person ($)" type="number" class="mb-2" />
          <v-text-field v-model.number="form.maxSeats" label="Max seats" type="number" class="mb-2" />
          <v-checkbox v-model="form.isWaitlist" label="Waitlist slot" />
          <v-checkbox v-model="form.active" label="Active" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAddDialog = false; editingSlot = null">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveSlot">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Delete this carpool slot?</v-card-title>
        <v-card-text v-if="deletingSlot">
          {{ deletingSlot.date }} · {{ deletingSlot.airport }} · {{ deletingSlot.time }}
          <span v-if="deletingSlot.dancers && deletingSlot.dancers.length > 0" class="red--text">
            <br>Warning: {{ deletingSlot.dancers.length }} dancer(s) are signed up for this slot.
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark :loading="deleting" @click="deleteSlot">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { carpoolDateSortKey } from '@/config/iabf-festival-dates'

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      seeding: false,
      showAddDialog: false,
      showDeleteDialog: false,
      showDancerDialog: false,
      showMakeCarpoolDialog: false,
      editingSlot: null,
      deletingSlot: null,
      targetSlot: null,
      makeCarpoolWaitlistSlot: null,
      makeCarpoolEntry: null,
      makeCarpoolDirection: null,
      saving: false,
      deleting: false,
      addingDancer: false,
      makingCarpool: false,
      removingId: null,
      showMissingDetails: false,
      copiedMissing: false,
      addDancerForm: { dancer: null, location: 'atlantic', isGuest: false, guestName: '', airport: null, requestedTime: '' },
      makeCarpoolForm: { date: '', airport: '', time: '', pricePerPerson: 0, maxSeats: 4 },
      locationOptions: [
        { text: 'Atlantic Center for the Arts (onsite)', value: 'atlantic' },
        { text: 'SpringHill Suites (offsite)', value: 'salty_mermaid' }
      ],
      form: this.blankForm(),
      headers: [
        { text: 'Date', value: 'date' },
        { text: 'Airport', value: 'airport' },
        { text: 'Time', value: 'time' },
        { text: 'Price', value: 'pricePerPerson' },
        { text: 'Dancers', value: 'dancers', sortable: false },
        { text: 'Seats', value: 'seats', sortable: false },
        { text: 'Status', value: 'isWaitlist', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    slots () {
      return this.$store.getters.iabfCarpoolSlots
    },
    arrivalSlots () {
      return this.slots.filter(s => s.direction === 'arrival')
    },
    departureSlots () {
      return this.slots.filter(s => s.direction === 'departure')
    },
    arrivalRegularSlots () {
      return this.arrivalSlots.filter(s => !s.isWaitlist).slice().sort(this.compareSlotsChronologically)
    },
    departureRegularSlots () {
      return this.departureSlots.filter(s => !s.isWaitlist).slice().sort(this.compareSlotsChronologically)
    },
    arrivalWaitlistSlot () {
      return this.arrivalSlots.find(s => s.isWaitlist) || null
    },
    departureWaitlistSlot () {
      return this.departureSlots.find(s => s.isWaitlist) || null
    },
    arrivalWaitlistEntries () {
      return this.arrivalWaitlistSlot ? (this.arrivalWaitlistSlot.dancers || []) : []
    },
    departureWaitlistEntries () {
      return this.departureWaitlistSlot ? (this.departureWaitlistSlot.dancers || []) : []
    },
    allDancers () {
      return this.$store.getters.loadedIABFDancers
    },
    dancerOptions () {
      const slotDancerIds = new Set((this.targetSlot ? this.targetSlot.dancers || [] : []).map(d => d.id))
      return this.allDancers.filter(d => !slotDancerIds.has(d.id))
    },
    canSubmitAddDancer () {
      if (this.addDancerForm.isGuest) return !!this.addDancerForm.guestName.trim()
      return !!this.addDancerForm.dancer
    },
    travelByPersonId () {
      const map = new Map()
      for (const t of (this.$store.getters.loadedAllTravelDetails || [])) {
        if (t.personId) map.set(t.personId, t)
      }
      return map
    },
    dancerInAnySlot () {
      const set = new Set()
      for (const s of this.slots) {
        for (const d of (s.dancers || [])) set.add(d.id)
      }
      return set
    },
    missingCarpoolEntries () {
      const out = []
      for (const d of this.allDancers) {
        if (!d.email) continue
        if (this.dancerInAnySlot.has(d.id)) continue
        const tr = this.travelByPersonId.get(d.id) || {}
        const aType = tr.arrivalType || ''
        const dType = tr.departureType || ''
        // Skip dancers who are driving both directions — they don't need a
        // carpool either way.
        if (aType === 'Car' && dType === 'Car') continue
        out.push({
          id: d.id,
          name: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email,
          email: d.email,
          aType,
          dType
        })
      }
      return out.sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
    this.$store.dispatch('loadIABFDancers')
    this.$store.dispatch('loadAllTravelDetails')
  },
  methods: {
    compareSlotsChronologically (a, b) {
      const da = this.dateSortKey(a.date)
      const dbKey = this.dateSortKey(b.date)
      if (da !== dbKey) return da - dbKey
      const ta = this.parseClockTime(a.time)
      const tb = this.parseClockTime(b.time)
      return (ta === null ? Infinity : ta) - (tb === null ? Infinity : tb)
    },
    dateSortKey (dateStr) {
      return carpoolDateSortKey(dateStr, this.adminFestivalYear)
    },
    parseClockTime (str) {
      if (!str) return null
      const s = String(str).toLowerCase().trim()
      const m = s.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/)
      if (!m) return null
      let h = parseInt(m[1], 10)
      const min = parseInt(m[2] || '0', 10)
      const mer = m[3]
      if (mer === 'pm' && h < 12) h += 12
      if (mer === 'am' && h === 12) h = 0
      return h * 60 + min
    },
    async copyMissingEmails () {
      const emails = this.missingCarpoolEntries.map(d => d.email).join(', ')
      try {
        await navigator.clipboard.writeText(emails)
      } catch (e) {
        const ta = document.createElement('textarea')
        ta.value = emails
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy') } catch (err) { /* noop */ }
        document.body.removeChild(ta)
      }
      this.copiedMissing = true
      setTimeout(() => { this.copiedMissing = false }, 1800)
    },
    isGuestRider (rider) {
      return !!rider.isGuest || String(rider.id || '').startsWith('guest-')
    },
    async saveRiderPhone (slot, rider, raw) {
      try {
        await this.$store.dispatch('updateCarpoolRiderPhone', {
          slotId: slot.id,
          riderId: rider.id,
          phone: raw
        })
        await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      } catch (e) {
        console.error('updateCarpoolRiderPhone failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save phone. ' + (e && e.message ? e.message : ''))
      }
    },
    async saveRiderFlightTime (slot, rider, raw) {
      try {
        await this.$store.dispatch('updateCarpoolRiderFlightTime', {
          slotId: slot.id,
          riderId: rider.id,
          flightTime: raw
        })
        await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      } catch (e) {
        console.error('updateCarpoolRiderFlightTime failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save flight time. ' + (e && e.message ? e.message : ''))
      }
    },
    riderFlightTime (rider, direction) {
      // For regular dancers, pull from their travel details. For guests
      // (faculty added directly to a slot), use the time stored on the
      // slot entry itself.
      const travel = this.travelByPersonId.get(rider.id) || {}
      const raw = direction === 'arrival'
        ? (travel.arrivalTime || rider.guestFlightTime || '')
        : (travel.departureTime || rider.guestFlightTime || '')
      return this.formatTime12(raw)
    },
    formatTime12 (raw) {
      const s = String(raw || '').trim()
      if (!s) return ''
      if (/am|pm/i.test(s)) return s
      const m = s.match(/^(\d{1,2}):(\d{2})$/)
      if (!m) return s
      let h = parseInt(m[1], 10)
      const min = m[2]
      if (!isFinite(h) || h < 0 || h > 23) return s
      const mer = h >= 12 ? 'PM' : 'AM'
      h = h % 12
      if (h === 0) h = 12
      return `${h}:${min} ${mer}`
    },
    blankForm () {
      return { direction: 'arrival', date: '', airport: 'MCO', time: '', pricePerPerson: 60, maxSeats: 4, isWaitlist: false, active: true }
    },
    setPrice () {
      const prices = { MCO: 60, DAB: 25 }
      this.form.pricePerPerson = prices[this.form.airport] || 0
    },
    setMakeCarpoolPrice () {
      const prices = { MCO: 60, DAB: 25 }
      this.makeCarpoolForm.pricePerPerson = prices[this.makeCarpoolForm.airport] || 0
    },
    editSlot (slot) {
      this.editingSlot = slot
      this.form = {
        direction: slot.direction,
        date: slot.date,
        airport: slot.airport,
        time: slot.time,
        pricePerPerson: slot.pricePerPerson,
        maxSeats: slot.maxSeats,
        isWaitlist: slot.isWaitlist || false,
        active: slot.active !== false
      }
      this.showAddDialog = true
    },
    async saveSlot () {
      this.saving = true
      if (this.editingSlot) {
        await this.$store.dispatch('updateCarpoolSlot', { id: this.editingSlot.id, ...this.form })
      } else {
        await this.$store.dispatch('createCarpoolSlot', this.form)
      }
      await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      this.showAddDialog = false
      this.editingSlot = null
      this.form = this.blankForm()
      this.saving = false
    },
    confirmDelete (slot) {
      this.deletingSlot = slot
      this.showDeleteDialog = true
    },
    async deleteSlot () {
      this.deleting = true
      await this.$store.dispatch('deleteCarpoolSlot', this.deletingSlot.id)
      await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      this.showDeleteDialog = false
      this.deletingSlot = null
      this.deleting = false
    },
    async seedSlots () {
      this.seeding = true
      try {
        const result = await this.$store.dispatch('seedCarpoolSlots')
        await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
        if (result && result.error) {
          alert(result.error)
        }
      } catch (e) {
        console.error(e)
        alert(e.message || 'Could not seed slots from last year.')
      }
      this.seeding = false
    },
    openAddDancer (slot) {
      this.targetSlot = slot
      this.addDancerForm = { dancer: null, location: 'atlantic', isGuest: false, guestName: '', airport: null, requestedTime: '' }
      this.showDancerDialog = true
    },
    async addDancer () {
      if (!this.canSubmitAddDancer) return
      this.addingDancer = true
      const slot = this.targetSlot
      const form = this.addDancerForm
      if (form.isGuest) {
        await this.$store.dispatch('addGuestToCarpoolSlot', {
          slotId: slot.id,
          name: form.guestName.trim(),
          location: slot.isWaitlist ? undefined : form.location,
          airport: slot.isWaitlist ? (form.airport || undefined) : undefined,
          requestedTime: slot.isWaitlist ? (form.requestedTime || undefined) : undefined
        })
      } else {
        const dancer = form.dancer
        const name = `${dancer.firstName || ''} ${dancer.lastName ? dancer.lastName[0] + '.' : ''}`.trim()
        await this.$store.dispatch('joinCarpoolSlot', {
          slotId: slot.id,
          direction: slot.direction,
          dancerId: dancer.id,
          dancerName: name,
          carpoolArrivalDropoff: !slot.isWaitlist && slot.direction === 'arrival' ? form.location : undefined,
          carpoolDeparturePickup: !slot.isWaitlist && slot.direction === 'departure' ? form.location : undefined,
          airport: slot.isWaitlist ? (form.airport || undefined) : undefined,
          requestedTime: slot.isWaitlist ? (form.requestedTime || undefined) : undefined
        })
      }
      await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      this.showDancerDialog = false
      this.addingDancer = false
    },
    async removeDancer (slot, dancer) {
      this.removingId = dancer.id + slot.id
      await this.$store.dispatch('leaveCarpoolSlot', {
        slotId: slot.id,
        direction: slot.direction,
        dancerId: dancer.id
      })
      await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      this.removingId = null
    },
    openMakeCarpool (waitlistSlot, entry, direction) {
      this.makeCarpoolWaitlistSlot = waitlistSlot
      this.makeCarpoolEntry = entry
      this.makeCarpoolDirection = direction
      const prices = { MCO: 60, DAB: 25 }
      this.makeCarpoolForm = {
        date: '',
        airport: entry.airport || '',
        time: entry.requestedTime || '',
        pricePerPerson: prices[entry.airport] || 0,
        maxSeats: 4
      }
      this.showMakeCarpoolDialog = true
    },
    async confirmMakeCarpool () {
      this.makingCarpool = true
      const entry = this.makeCarpoolEntry
      const direction = this.makeCarpoolDirection
      const location = entry.dropoff || entry.pickup || 'atlantic'

      // 1. Create the new slot
      const newSlotId = await this.$store.dispatch('createCarpoolSlot', {
        direction,
        date: this.makeCarpoolForm.date,
        airport: this.makeCarpoolForm.airport,
        time: this.makeCarpoolForm.time,
        pricePerPerson: this.makeCarpoolForm.pricePerPerson,
        maxSeats: this.makeCarpoolForm.maxSeats,
        sortOrder: 999,
        isWaitlist: false,
        active: true
      })

      // 2. Remove dancer from waitlist slot
      await this.$store.dispatch('leaveCarpoolSlot', {
        slotId: this.makeCarpoolWaitlistSlot.id,
        direction,
        dancerId: entry.id
      })

      // 3. Add dancer to the new slot
      await this.$store.dispatch('joinCarpoolSlot', {
        slotId: newSlotId,
        direction,
        dancerId: entry.id,
        dancerName: entry.name,
        carpoolArrivalDropoff: direction === 'arrival' ? location : undefined,
        carpoolDeparturePickup: direction === 'departure' ? location : undefined
      })

      await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      this.showMakeCarpoolDialog = false
      this.makingCarpool = false
    },
    driveTimeLabel (airport) {
      return { MCO: '1 hr 15 min', DAB: '25 min' }[airport] || ''
    },
    driveTimeMins (airport) {
      return { MCO: 75, DAB: 25 }[airport] || 0
    },
    parseTime (str) {
      if (!str) return null
      const s = str.toString().trim().toLowerCase()
      let m = s.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/)
      if (m) {
        let h = parseInt(m[1])
        const min = parseInt(m[2])
        const ap = m[3]
        if (ap === 'pm' && h < 12) h += 12
        if (ap === 'am' && h === 12) h = 0
        return h * 60 + min
      }
      m = s.match(/^(\d{1,2})\s*(am|pm)/)
      if (m) {
        let h = parseInt(m[1])
        const ap = m[2]
        if (ap === 'pm' && h < 12) h += 12
        if (ap === 'am' && h === 12) h = 0
        return h * 60
      }
      return null
    },
    computedArrivalTime (slot) {
      if (slot.direction !== 'departure' || slot.isWaitlist) return null
      const leavesMins = this.parseTime(slot.time)
      if (leavesMins === null) return null
      const arrivalMins = leavesMins + this.driveTimeMins(slot.airport)
      const h24 = Math.floor(arrivalMins / 60)
      const min = arrivalMins % 60
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12
      const ap = h24 < 12 ? 'am' : 'pm'
      return h12 + ':' + String(min).padStart(2, '0') + ap
    }
  }
}
</script>

<style scoped>
.section-heading {
  font-family: Quattrocento, Georgia, serif;
  font-size: 20px;
  font-weight: 700;
  margin: 24px 0 12px;
  color: #333;
}
.drive-time-note {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
}
.dancer-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-bottom: 4px;
}

.missing-panel {
  background: #fff3e0;
  border: 1px solid #f9a825;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 0 0 16px;
}

.missing-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.missing-sub {
  font-size: 12px;
  color: #6a3e00;
  font-style: italic;
  margin-left: 8px;
}

.missing-actions {
  display: flex;
  gap: 6px;
}

.missing-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 4px 12px;
}

.missing-list li {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  padding: 4px 0;
  border-bottom: 1px dashed #f4c63d;
}

.missing-name {
  font-weight: 700;
  color: #1a1a1a;
}

.missing-email {
  color: #555;
}

.missing-travel {
  font-size: 11px;
  color: #6a3e00;
  font-style: italic;
}
.dancer-name {
  font-size: 13px;
}
.dancer-location {
  font-size: 11px;
  color: #888;
}
.dancer-flight {
  font-size: 11px;
  color: #00695c;
  font-weight: 600;
}
.faculty-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #efebe9;
  color: #5d4037;
  padding: 1px 5px;
  border-radius: 6px;
  margin-left: 4px;
}
.faculty-edit {
  display: flex;
  gap: 6px;
  margin-top: 2px;
  width: 100%;
  padding-left: 4px;
}
.faculty-input {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  padding: 2px 6px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  background: #fff;
}
.faculty-input:focus {
  border-color: #267E7D;
  outline: none;
}

/* Waitlist section */
.waitlist-section {
  margin-top: 4px;
}
.waitlist-section-label {
  font-family: Quattrocento, Georgia, serif;
  font-size: 14px;
  font-weight: 700;
  color: #b45309;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}
.waitlist-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.waitlist-card {
  background: #fff8e1;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 14px 16px;
  min-width: 220px;
  max-width: 280px;
  display: flex;
  flex-direction: column;
}
.waitlist-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
}
.waitlist-card-details {
  font-size: 13px;
  color: #555;
  margin-bottom: 4px;
}
.waitlist-card-airport {
  font-weight: 700;
}
.waitlist-card-sep {
  margin: 0 4px;
  color: #aaa;
}
.waitlist-card-location {
  font-size: 12px;
  color: #888;
}
</style>
